import { UIColor, UIView, YES } from "uicore-ts"
import { RHBadgeView } from "../../Custom components/RH/RHBadgeView"
import { RHColors, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHCompanyMemberPresentation } from "./CompanyMemberPresentation"


// The company-role key vocabulary, owned here because this view is the single
// authority that projects role keys into their Estonian display labels. The
// canonical union also lives — unexported — in SocketClientFunctions.d.ts as
// `UserRoles`; callers that receive roles straight off the socket still hand in
// `string[]` (e.g. ListAllUsersForCompany types `roles` as `string[]`), so the
// public methods below stay `string[]` and only role arrays we build ourselves
// are typed against this union.
export type CompanyRole = "administrator" | "procurer" | "auditor" | "consultant" | "offerer"


interface CompanyRoleBadgeAppearance {
    label: string
    backgroundColor: UIColor
    textColor: UIColor
    hoverText?: string
}


/**
 * Vertical stack of company-role badges for one member — shared by the members
 * table, the join-requests table and the administration status strip. The
 * administrator role collapses to a single "Peakasutaja" badge (the bundled
 * hankija/pakkuja access is implied and explained via the badge hover text),
 * so the automatic bundle does not read as three independent grants.
 */
export class CompanyRoleBadgesView extends UIView {

    _badgeViews: RHBadgeView[] = []


    // The one Estonian projection of a member's role set — the same labels the
    // badges show. The tables also feed this into their search corpus so that
    // searching "Peakasutaja" matches what the user sees, not raw backend keys.
    static displayLabelsForRoles(
        roles: string[],
        includesUnusedRoles: boolean = YES
    ): string[] {
        return CompanyRoleBadgesView._badgeAppearancesForRoles(
            roles,
            includesUnusedRoles
        ).map(
            badgeAppearance => badgeAppearance.label
        )
    }


    updateWithRoles(
        roles: string[],
        isEligibleToActAsProcurer: boolean,
        includesUnusedRoles: boolean = YES
    ) {
        const badgeAppearances = CompanyRoleBadgesView._badgeAppearancesForRoles(
            roles,
            includesUnusedRoles,
            RHCompanyMemberPresentation.administratorRoleExplanationForProcurerEligibility(
                isEligibleToActAsProcurer
            )
        )

        while (this._badgeViews.length > badgeAppearances.length) {
            const badgeView = this._badgeViews.pop()
            badgeView?.removeFromSuperview()
        }

        badgeAppearances.forEach((badgeAppearance, index) => {
            let badgeView = this._badgeViews[index]
            if (!badgeView) {
                badgeView = new RHBadgeView().addedAsSubviewToView(this)
                this._badgeViews[index] = badgeView
            }

            badgeView.text = badgeAppearance.label
            badgeView.setVariant(badgeAppearance.backgroundColor, badgeAppearance.textColor)
            badgeView.hoverText = badgeAppearance.hoverText
        })

        this.setNeedsLayout()
    }


    static _badgeAppearancesForRoles(
        roles: string[],
        includesUnusedRoles: boolean = YES,
        administratorHoverText?: string
    ): CompanyRoleBadgeAppearance[] {
        const badgeAppearances: CompanyRoleBadgeAppearance[] = []

        if (roles.contains("administrator")) {
            badgeAppearances.push({
                label: RHCompanyAdministrationViewTokens.roleDisplayLabels.administrator,
                backgroundColor: RHColors.primaryBlueSurface,
                textColor: RHColors.primaryBlue,
                hoverText: administratorHoverText
            })
        }
        else {
            if (roles.contains("procurer")) {
                badgeAppearances.push(this._neutralBadgeAppearanceWithLabel(
                    RHCompanyAdministrationViewTokens.roleDisplayLabels.procurer
                ))
            }
            if (roles.contains("offerer")) {
                badgeAppearances.push(this._neutralBadgeAppearanceWithLabel(
                    RHCompanyAdministrationViewTokens.roleDisplayLabels.offerer
                ))
            }
        }

        if (includesUnusedRoles) {
            if (roles.contains("auditor")) {
                badgeAppearances.push(this._neutralBadgeAppearanceWithLabel(
                    RHCompanyAdministrationViewTokens.roleDisplayLabels.auditor
                ))
            }
            if (roles.contains("consultant")) {
                badgeAppearances.push(this._neutralBadgeAppearanceWithLabel(
                    RHCompanyAdministrationViewTokens.roleDisplayLabels.consultant
                ))
            }
        }

        if (badgeAppearances.length === 0) {
            badgeAppearances.push(this._neutralBadgeAppearanceWithLabel(
                RHCompanyAdministrationViewTokens.missingRoleDisplayLabel
            ))
        }

        return badgeAppearances
    }


    static _neutralBadgeAppearanceWithLabel(label: string): CompanyRoleBadgeAppearance {
        return {
            label: label,
            backgroundColor: RHColors.surfaceSubtle,
            textColor: RHColors.slateDarkest
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        bounds
            .rectangleWithHeight(this.intrinsicContentHeight(bounds.width), 0.5)
            .framesByDistributingViewsAsColumn(
                this._badgeViews,
                RHSpacing.tiny,
                this._badgeViews
            )
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        let contentHeight = 0
        for (let index = 0; index < this._badgeViews.length; index++) {
            if (index > 0) {
                contentHeight += RHSpacing.tiny
            }
            contentHeight += this._badgeViews[index].intrinsicContentHeight(constrainingWidth)
        }
        return contentHeight
    }


    override intrinsicContentWidth(constrainingHeight = 0) {
        let widestBadgeWidth = 0
        for (const badgeView of this._badgeViews) {
            widestBadgeWidth = Math.max(widestBadgeWidth, badgeView.intrinsicContentWidth(constrainingHeight))
        }
        return widestBadgeWidth
    }

}
